import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  ArrowLeft, 
  UserPlus,
  User,
  Building2,
  Search,
  Edit,
  Trash2,
  CreditCard,
  Hash,
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement() {
  const { staff } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form
  const [createForm, setCreateForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    personal_number: "",
    badge_id: "",
    status: "ACTIVE"
  });
  const [editForm, setEditForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const response = await axios.get(`${API}/users${params}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchUsers]);

  const handleCreate = async () => {
    if (!createForm.first_name || !createForm.last_name || !createForm.company || !createForm.personal_number) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    setFormLoading(true);
    try {
      await axios.post(`${API}/users`, createForm);
      toast.success("Benutzer erfolgreich angelegt");
      setShowCreateModal(false);
      setCreateForm({
        first_name: "",
        last_name: "",
        company: "",
        personal_number: "",
        badge_id: "",
        status: "ACTIVE"
      });
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Anlegen";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editForm);
      toast.success("Benutzer erfolgreich aktualisiert");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Aktualisieren";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success("Benutzer erfolgreich gelöscht");
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Löschen";
      toast.error(message);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Bitte Datei auswählen");
      return;
    }

    setImportLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/import/users`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      fetchUsers();
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} Benutzer importiert`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Import";
      toast.error(message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async (format) => {
    try {
      const response = await axios.get(`${API}/import/template?format=${format}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import_vorlage.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const handleExportUsers = async (format) => {
    try {
      const response = await axios.get(`${API}/export/users?format=${format}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `benutzer.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export erfolgreich");
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      company: user.company,
      badge_id: user.badge_id || "",
      status: user.status
    });
    setShowEditModal(true);
  };

  const isAdmin = staff?.role === "ADMIN";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="user-management">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Benutzerverwaltung
                </h1>
                <p className="text-sm text-slate-500">Mitarbeiter-Stammdaten (ohne Login)</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suche nach Name, Personalnr..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              {/* Import/Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Import/Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => {
                    setImportFile(null);
                    setImportResult(null);
                    setShowImportModal(true);
                  }}>
                    <Upload className="h-4 w-4 mr-2" />
                    Importieren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportUsers("csv")}>
                    <FileText className="h-4 w-4 mr-2" />
                    Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportUsers("excel")}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button onClick={() => setShowCreateModal(true)} data-testid="create-user-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Neuer Benutzer
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Benutzerliste
              <Badge variant="outline" className="ml-2">{users.length} Einträge</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Personalnr.</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Ausweis-ID</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-mono">{user.personal_number}</TableCell>
                      <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                      <TableCell>{user.company}</TableCell>
                      <TableCell className="font-mono">{user.badge_id || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === "ACTIVE" ? "default" : "secondary"}>
                          {user.status === "ACTIVE" ? "Aktiv" : user.status === "INACTIVE" ? "Inaktiv" : "Gesperrt"}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(user)}
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteDialog(true);
                              }}
                              title="Löschen"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-slate-500 py-8">
                        Keine Benutzer gefunden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Hinweis:</strong> Benutzer sind Mitarbeiter, die Geräte ausleihen können. 
            Sie haben keinen Login-Zugang zum System. Zur Identifikation wird die Ausweis-ID (Badge) 
            oder die Personalnummer verwendet.
          </p>
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Neuen Benutzer anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname *</Label>
                <Input
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                  placeholder="Vorname"
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname *</Label>
                <Input
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                  placeholder="Nachname"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Firma *
              </Label>
              <Input
                value={createForm.company}
                onChange={(e) => setCreateForm({...createForm, company: e.target.value})}
                placeholder="Firmenname"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Personalnummer *
              </Label>
              <Input
                value={createForm.personal_number}
                onChange={(e) => setCreateForm({...createForm, personal_number: e.target.value})}
                placeholder="Personalnummer"
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Ausweis-ID (Badge)
              </Label>
              <Input
                value={createForm.badge_id}
                onChange={(e) => setCreateForm({...createForm, badge_id: e.target.value})}
                placeholder="ID vom Mitarbeiterausweis"
                className="font-mono"
              />
              <p className="text-xs text-slate-500">Wird für die Ausgabe per Kartenleser benötigt</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={formLoading}>
              {formLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Anlegen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Benutzer bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname</Label>
                <Input
                  value={editForm.first_name || ""}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname</Label>
                <Input
                  value={editForm.last_name || ""}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Firma</Label>
              <Input
                value={editForm.company || ""}
                onChange={(e) => setEditForm({...editForm, company: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Ausweis-ID (Badge)</Label>
              <Input
                value={editForm.badge_id || ""}
                onChange={(e) => setEditForm({...editForm, badge_id: e.target.value})}
                className="font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({...editForm, status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Aktiv</SelectItem>
                  <SelectItem value="INACTIVE">Inaktiv</SelectItem>
                  <SelectItem value="BLOCKED">Gesperrt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleEdit} disabled={formLoading}>
              {formLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Speichern"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Benutzer <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Benutzer importieren
            </DialogTitle>
            <DialogDescription>
              Importieren Sie Benutzer aus einer CSV- oder Excel-Datei
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File upload */}
            <div className="space-y-2">
              <Label>Datei auswählen</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  ref={fileInputRef}
                  onChange={(e) => {
                    setImportFile(e.target.files[0]);
                    setImportResult(null);
                  }}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">
                Unterstützte Formate: CSV, Excel (.xlsx, .xls)
              </p>
            </div>
            
            {/* Template download */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium mb-2">Vorlage herunterladen</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate("csv")}>
                  <FileText className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate("excel")}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Excel
                </Button>
              </div>
            </div>
            
            {/* Import result */}
            {importResult && (
              <div className={`p-3 rounded-lg ${importResult.imported > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.imported > 0 ? (
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  )}
                  <span className="font-medium text-sm">Import-Ergebnis</span>
                </div>
                <div className="text-sm space-y-1">
                  <p className="text-emerald-700">{importResult.imported} Benutzer importiert</p>
                  {importResult.skipped > 0 && (
                    <p className="text-amber-700">{importResult.skipped} übersprungen (bereits vorhanden)</p>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div className="text-red-600 mt-2">
                      <p className="font-medium">Fehler:</p>
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Schließen
            </Button>
            <Button onClick={handleImport} disabled={importLoading || !importFile}>
              {importLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Importieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
